import React, { useEffect, useMemo, useState } from "react";
import { MessageCircle, Copy, Check, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * AdminWhatsappShareModal
 * Shown right after a new car is saved (or on demand) to help Rocco send a
 * broadcast to his WhatsApp contacts/lists with 1 click.
 *
 * It:
 *  - Loads the public "site url" and "whatsapp number" from the API
 *  - Builds a ready-to-send message with brand/model/year/km/price + link
 *  - Provides a "Copia messaggio" button (clipboard) and an "Apri WhatsApp"
 *    button that launches wa.me with the message pre-filled (so from mobile
 *    Rocco chooses the broadcast list, from desktop opens WhatsApp Web).
 */
export const AdminWhatsappShareModal = ({ car, open, onClose }) => {
  const [siteUrl, setSiteUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState("");

  useEffect(() => {
    if (!open) return;
    // Public site URL for building the car detail deep link. Prefer settings.public_url,
    // fallback to window origin (works because admin is always browsed from the site).
    (async () => {
      try {
        const r = await api.get("/settings");
        setWhatsappNumber((r.data.whatsapp_number || "").trim());
        setSiteUrl((r.data.public_url || window.location.origin).trim());
      } catch {
        setSiteUrl(window.location.origin);
      }
    })();
  }, [open]);

  const message = useMemo(() => {
    if (!car) return "";
    const price = typeof car.price === "number"
      ? `€ ${Math.round(car.price).toLocaleString("it-IT")}`
      : car.price || "-";
    const km = typeof car.km === "number"
      ? `${Math.round(car.km).toLocaleString("it-IT")} km`
      : car.km || "";
    const specs = [car.year, km, car.fuel, car.transmission].filter(Boolean).join(" · ");
    const link = siteUrl ? `${siteUrl}/auto/${car.id}` : "";
    return (
      `🚗 *Nuova auto in concessionaria*\n\n` +
      `*${(car.brand || "").toUpperCase()} ${car.model || ""}*\n` +
      `${specs}\n` +
      `Prezzo: *${price}*\n` +
      (car.ref_code ? `Rif. ${car.ref_code}\n` : "") +
      `\nVieni a vederla o contattami per informazioni.\n` +
      (link ? `\n👉 ${link}\n` : "") +
      `\n_— Rocco · Guzzardella Auto_`
    );
  }, [car, siteUrl]);

  const copyMsg = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success("Messaggio copiato negli appunti");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Impossibile copiare — seleziona e copia a mano");
    }
  };

  const openWA = () => {
    // wa.me only pre-fills the message; the user then chooses the destination
    // (single contact, group or broadcast list). Without a phone number, it
    // opens the WhatsApp share picker.
    const encoded = encodeURIComponent(message);
    const url = whatsappNumber
      ? `https://wa.me/${whatsappNumber.replace(/[^\d]/g, "")}?text=${encoded}`
      : `https://api.whatsapp.com/send?text=${encoded}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!open || !car) return null;

  return (
    <div
      className="fixed inset-0 z-[9997] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wa-share-title"
      data-testid="admin-wa-share-modal"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-midnight border-2 border-[#25D366] shadow-2xl rounded-sm px-6 py-6">
        <button
          type="button"
          onClick={onClose}
          aria-label="Chiudi"
          className="absolute top-3 right-3 h-8 w-8 flex items-center justify-center text-white/60 hover:text-white transition-colors"
          data-testid="wa-share-close"
        >
          <X size={18} />
        </button>
        <div className="flex items-center gap-3 mb-5">
          <div className="h-11 w-11 rounded-full bg-[#25D366] flex items-center justify-center">
            <MessageCircle size={20} className="text-white" strokeWidth={2.5} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#25D366] font-bold">
              Broadcast WhatsApp
            </p>
            <h3 id="wa-share-title" className="font-display text-xl text-white leading-tight">
              Manda l'auto ai contatti
            </h3>
          </div>
        </div>
        <p className="text-xs text-white/60 leading-relaxed mb-4">
          Copia il messaggio o apri WhatsApp — potrai scegliere la tua <b>Lista Broadcast</b> o
          un gruppo dei clienti VIP.
        </p>
        <div className="bg-black/40 border border-white/10 rounded-sm p-4 mb-5 max-h-[240px] overflow-y-auto">
          <pre className="whitespace-pre-wrap text-sm text-white/90 font-mono leading-relaxed">
{message}
          </pre>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={copyMsg}
            className="flex items-center justify-center gap-2 py-3 px-4 bg-white/10 hover:bg-white/20 text-white text-xs uppercase tracking-widest font-bold border border-white/20 transition-colors rounded-sm"
            data-testid="wa-share-copy-btn"
          >
            {copied ? <Check size={15} /> : <Copy size={15} />}
            {copied ? "Copiato!" : "Copia messaggio"}
          </button>
          <button
            type="button"
            onClick={openWA}
            className="flex items-center justify-center gap-2 py-3 px-4 bg-[#25D366] hover:bg-[#1FB855] text-white text-xs uppercase tracking-widest font-bold transition-colors rounded-sm shadow-lg"
            data-testid="wa-share-open-btn"
          >
            <ExternalLink size={15} strokeWidth={2.5} />
            Apri WhatsApp
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full text-xs text-white/50 hover:text-white/80 transition-colors uppercase tracking-widest py-2"
          data-testid="wa-share-later-btn"
        >
          Lo faccio più tardi
        </button>
      </div>
    </div>
  );
};

export default AdminWhatsappShareModal;
